import { useEffect, useState, useCallback } from "react";
import FirewallLists from "./FirewallLists";
import { useConfirm } from "./ConfirmDialog";

// ── API helper ────────────────────────────────────────────────────────────────
async function apiFetch(path, opts = {}) {
  const token = localStorage.getItem("token");
  const res = await fetch(`/v1/ufw${path}`, {
    method: opts.method || "GET",
    headers: {
      "Authorization": `Bearer ${token || ""}`,
      "Content-Type": "application/json",
    },
    body: opts.body || undefined,
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || `HTTP ${res.status}`);
  }
  return res.status === 204 ? null : res.json();
}

// ── Paleta de colores ─────────────────────────────────────────────────────────
const C = {
  bg:      "var(--bg,#0f172a)",
  surface: "var(--surface,#1e293b)",
  border:  "var(--border,#334155)",
  text:    "var(--text,#f1f5f9)",
  muted:   "var(--muted,#94a3b8)",
  accent:  "var(--accent,#38bdf8)",
  green:   "#22c55e",
  red:     "#ef4444",
  yellow:  "#f59e0b",
};

const ACTION_COLOR = { ALLOW: C.green, DENY: C.red, LIMIT: C.yellow, REJECT: C.red };
const NODE_ICONS   = {
  pbx: "🖥", ha: "🔄", sbc: "🛡", panel: "🌐", media: "🎵",
};

// ── Badge pequeño ─────────────────────────────────────────────────────────────
function Badge({ children, color }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4,
      background: `${color}22`, color, letterSpacing: ".4px",
      textTransform: "uppercase", border: `1px solid ${color}44`,
    }}>{children}</span>
  );
}

// ── Modal agregar regla ───────────────────────────────────────────────────────
function RuleModal({ nodeName, nodeKey, editRule, onSave, onClose }) {
  const isEdit = !!editRule;
  const [form, setForm] = useState(isEdit ? {
    action:    (editRule.action || "allow").toLowerCase(),
    direction: (editRule.direction || "in").toLowerCase(),
    proto:     editRule.proto || "any",
    port:      editRule.port || "",
    from_ip:   (editRule.from && editRule.from !== "Anywhere") ? editRule.from : "",
    to_ip:     "",
    comment:   editRule.comment || "",
  } : {
    action: "allow", direction: "in", proto: "tcp",
    port: "", from_ip: "", to_ip: "", comment: "",
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function handleSave() {
    setSaving(true); setErr("");
    try {
      await apiFetch(
        isEdit ? `/rules/${nodeKey}/${editRule.num}` : `/rules/${nodeKey}`,
        {
          method: isEdit ? "PUT" : "POST",
          body: JSON.stringify({
            action:    form.action,
            direction: form.direction,
            proto:     form.proto,
            port:      form.port.trim() || null,
            from_ip:   form.from_ip.trim() || null,
            to_ip:     form.to_ip.trim()   || null,
            comment:   form.comment.trim() || null,
          }),
        });
      onSave();
    } catch(e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = {
    width: "100%", padding: "8px 10px", borderRadius: 7,
    border: `1px solid ${C.border}`, background: C.bg,
    color: C.text, fontSize: 13, outline: "none", boxSizing: "border-box",
  };
  const selectStyle = { ...inputStyle };
  const labelStyle = {
    fontSize: 11, color: C.muted, display: "block", marginBottom: 5,
    textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600,
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 14, padding: 28, width: 500, maxWidth: "95vw",
        maxHeight: "90vh", overflowY: "auto",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 22 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>
              {isEdit ? `Editar regla #${editRule.num}` : "Agregar regla UFW"}
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{nodeName}</div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", color: C.muted,
            fontSize: 22, cursor: "pointer", lineHeight: 1,
          }}>×</button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Acción + Dirección */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Acción</label>
              <select value={form.action} onChange={set("action")} style={selectStyle}>
                <option value="allow">ALLOW — Permitir</option>
                <option value="deny">DENY — Denegar (silencioso)</option>
                <option value="reject">REJECT — Rechazar (con aviso)</option>
                <option value="limit">LIMIT — Limitar (anti-brute-force)</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Dirección</label>
              <select value={form.direction} onChange={set("direction")} style={selectStyle}>
                <option value="in">IN — Entrante</option>
                <option value="out">OUT — Saliente</option>
              </select>
            </div>
          </div>

          {/* Puerto + Protocolo */}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Puerto (opcional)</label>
              <input
                value={form.port} onChange={set("port")}
                placeholder="ej: 5060  |  8000:9000  |  vacío = todos"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Protocolo</label>
              <select value={form.proto} onChange={set("proto")} style={selectStyle}>
                <option value="tcp">TCP</option>
                <option value="udp">UDP</option>
                <option value="any">Cualquiera</option>
              </select>
            </div>
          </div>

          {/* From IP */}
          <div>
            <label style={labelStyle}>Desde IP / Rango (vacío = cualquier origen)</label>
            <input
              value={form.from_ip} onChange={set("from_ip")}
              placeholder="ej: 192.168.0.0/24  |  10.0.0.5  |  181.39.8.165"
              style={inputStyle}
            />
            <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
              LAN: <code style={{ color: C.accent }}>192.168.0.0/24</code> &nbsp;·&nbsp;
              Linkotel: <code style={{ color: C.accent }}>181.39.8.165</code> &nbsp;·&nbsp;
              Público: dejar vacío
            </div>
          </div>

          {/* To IP */}
          <div>
            <label style={labelStyle}>Hacia IP / destino (vacío = any)</label>
            <input
              value={form.to_ip} onChange={set("to_ip")}
              placeholder="ej: 192.168.182.20  |  dejar vacío = any"
              style={inputStyle}
            />
          </div>

          {/* Comentario */}
          <div>
            <label style={labelStyle}>Comentario (opcional, máx. 50 chars)</label>
            <input
              value={form.comment} onChange={set("comment")}
              maxLength={50}
              placeholder="ej: SIP Linkotel carrier"
              style={inputStyle}
            />
          </div>

          {/* Preview del comando */}
          <div style={{
            background: C.bg, border: `1px solid ${C.border}`,
            borderRadius: 8, padding: "10px 14px",
          }}>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>
              COMANDO GENERADO
            </div>
            <code style={{ fontSize: 12, color: C.accent }}>
              sudo ufw {form.action}
              {form.direction === "out" ? " out" : ""}
              {" from "}{form.from_ip || "any"}
              {" to "}{form.to_ip || "any"}
              {form.port ? ` port ${form.port}` : ""}
              {form.proto !== "any" ? ` proto ${form.proto}` : ""}
              {form.comment ? ` comment "${form.comment}"` : ""}
            </code>
          </div>
        </div>

        {err && (
          <div style={{
            marginTop: 14, padding: "10px 14px", borderRadius: 8,
            background: `${C.red}15`, border: `1px solid ${C.red}44`,
            color: C.red, fontSize: 12, fontFamily: "monospace",
          }}>{err}</div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            padding: "8px 18px", borderRadius: 8,
            border: `1px solid ${C.border}`, background: "none",
            color: C.muted, cursor: "pointer", fontSize: 13,
          }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: "8px 22px", borderRadius: 8,
            border: "none", background: C.accent, color: "#0f172a",
            fontWeight: 700, cursor: saving ? "not-allowed" : "pointer",
            fontSize: 13, opacity: saving ? .7 : 1,
          }}>
            {saving ? (isEdit ? "Guardando…" : "Aplicando…") : (isEdit ? "Guardar cambios" : "Aplicar regla")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tabla de reglas ───────────────────────────────────────────────────────────
function RulesTable({ node, rules, onDelete, onEditClick, onAddClick, ufwActive, toggling, onToggle }) {
  const [deleting, setDeleting] = useState(null);
  // window.confirm no abre cartel en el navegador de Carlos y devuelve false: cancelaba solo.
  const [confirmar, dialogoConfirm] = useConfirm();

  async function handleDelete(num) {
    if (!(await confirmar(`¿Eliminar regla #${num} de ${node.name}?`))) return;
    setDeleting(num);
    try {
      await onDelete(num);
    } finally {
      setDeleting(null);
    }
  }

  return (
    <div>
      {dialogoConfirm}
      {/* Barra de acciones */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 16, flexWrap: "wrap", gap: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
            {NODE_ICONS[node.key] || "🖥"} {node.name}
          </span>
          <Badge color={ufwActive ? C.green : C.red}>
            {ufwActive ? "UFW Activo" : "UFW Inactivo"}
          </Badge>
          <span style={{ fontSize: 11, color: C.muted }}>{node.ip}</span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {/* Toggle UFW */}
          <button onClick={onToggle} disabled={toggling} style={{
            padding: "7px 14px", borderRadius: 7, fontSize: 12, fontWeight: 600,
            cursor: toggling ? "not-allowed" : "pointer",
            border: `1px solid ${ufwActive ? C.red : C.green}`,
            background: "none",
            color: ufwActive ? C.red : C.green,
            opacity: toggling ? .6 : 1,
          }}>
            {toggling ? "Aplicando…" : ufwActive ? "⏹ Desactivar UFW" : "▶ Activar UFW"}
          </button>
          {/* Agregar regla */}
          <button onClick={onAddClick} style={{
            padding: "7px 14px", borderRadius: 7, fontSize: 12, fontWeight: 700,
            cursor: "pointer", border: "none",
            background: C.accent, color: "#0f172a",
          }}>
            + Agregar regla
          </button>
        </div>
      </div>

      {!ufwActive && (
        <div style={{
          padding: "14px 18px", borderRadius: 8, marginBottom: 16,
          background: `${C.yellow}15`, border: `1px solid ${C.yellow}44`,
          color: C.yellow, fontSize: 13,
        }}>
          ⚠ UFW está <strong>inactivo</strong> en este nodo. Las reglas configuradas no se aplican hasta activarlo.
        </div>
      )}

      {rules.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "32px 0", color: C.muted, fontSize: 13,
        }}>
          {ufwActive ? "No hay reglas configuradas." : "UFW inactivo — sin reglas visibles."}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["#", "Destino / Puerto", "Acción", "Dirección", "Desde (From)", "Comentario", ""].map(h => (
                  <th key={h} style={{
                    padding: "8px 12px", textAlign: "left",
                    fontSize: 11, color: C.muted, fontWeight: 600,
                    textTransform: "uppercase", letterSpacing: ".4px",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rules.map(rule => (
                <tr key={rule.num} style={{
                  borderBottom: `1px solid ${C.border}22`,
                  transition: "background .15s",
                }}>
                  <td style={{ padding: "10px 12px", color: C.muted, fontFamily: "monospace" }}>
                    {rule.num}
                  </td>
                  <td style={{ padding: "10px 12px", color: C.text, fontFamily: "monospace" }}>
                    {rule.to}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <Badge color={ACTION_COLOR[rule.action] || C.muted}>
                      {rule.action}
                    </Badge>
                  </td>
                  <td style={{ padding: "10px 12px", color: C.muted, fontSize: 12 }}>
                    {rule.direction}
                  </td>
                  <td style={{ padding: "10px 12px", color: C.text, fontFamily: "monospace", fontSize: 12 }}>
                    {rule.from}
                  </td>
                  <td style={{ padding: "10px 12px", color: C.muted, fontSize: 12 }}>
                    {rule.comment || "—"}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                    {(() => {
                      const isF2B = (rule.comment || "").toLowerCase().includes("fail2ban");
                      return (
                        <button
                          onClick={() => onEditClick(rule)}
                          disabled={isF2B}
                          title={isF2B
                            ? "Regla gestionada por Fail2Ban — usá desbanear, no editar"
                            : `Editar regla #${rule.num}`}
                          style={{
                            background: "none", border: `1px solid ${C.accent}55`,
                            color: C.accent, borderRadius: 6, padding: "4px 10px",
                            fontSize: 12, marginRight: 8,
                            cursor: isF2B ? "not-allowed" : "pointer",
                            opacity: isF2B ? .35 : 1,
                          }}>
                          ✏️ Editar
                        </button>
                      );
                    })()}
                    <button
                      onClick={() => handleDelete(rule.num)}
                      disabled={deleting === rule.num}
                      title={`Eliminar regla #${rule.num}`}
                      style={{
                        background: "none", border: `1px solid ${C.red}55`,
                        color: C.red, borderRadius: 6, padding: "4px 10px",
                        fontSize: 12, cursor: deleting === rule.num ? "not-allowed" : "pointer",
                        opacity: deleting === rule.num ? .5 : 1,
                      }}>
                      {deleting === rule.num ? "…" : "🗑 Eliminar"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tabla de changelog ────────────────────────────────────────────────────────
function ChangelogTable({ entries }) {
  const ACTION_LABELS = {
    add_rule:    { label: "Regla agregada", color: C.green },
    edit_rule:   { label: "Regla editada",  color: C.accent },
    delete_rule: { label: "Regla eliminada", color: C.red },
    wl_add:      { label: "Whitelist +",    color: C.green },
    wl_del:      { label: "Whitelist −",    color: C.yellow },
    bl_add:      { label: "Blacklist +",    color: C.red },
    bl_del:      { label: "Blacklist −",    color: C.yellow },
    enable:      { label: "UFW activado",   color: C.green },
    disable:     { label: "UFW desactivado",color: C.yellow },
  };

  if (!entries.length) return (
    <div style={{ textAlign: "center", padding: "24px 0", color: C.muted, fontSize: 13 }}>
      Sin historial de cambios aún.
    </div>
  );

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${C.border}` }}>
            {["Fecha", "Nodo", "Acción", "Regla", "Usuario", "Estado"].map(h => (
              <th key={h} style={{
                padding: "7px 10px", textAlign: "left",
                fontSize: 10, color: C.muted, fontWeight: 600,
                textTransform: "uppercase", letterSpacing: ".4px",
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entries.map(e => {
            const meta = ACTION_LABELS[e.accion] || { label: e.accion, color: C.muted };
            return (
              <tr key={e.id} style={{ borderBottom: `1px solid ${C.border}22` }}>
                <td style={{ padding: "8px 10px", color: C.muted, whiteSpace: "nowrap" }}>
                  {e.created_at}
                </td>
                <td style={{ padding: "8px 10px", color: C.text }}>
                  {NODE_ICONS[e.node] || "🖥"} {e.node}
                  <span style={{ color: C.muted, fontSize: 10, marginLeft: 4 }}>
                    ({e.node_ip})
                  </span>
                </td>
                <td style={{ padding: "8px 10px" }}>
                  <Badge color={meta.color}>{meta.label}</Badge>
                </td>
                <td style={{ padding: "8px 10px", color: C.text, fontFamily: "monospace", fontSize: 11 }}>
                  {e.regla}
                </td>
                <td style={{ padding: "8px 10px", color: C.accent }}>
                  {e.username}
                </td>
                <td style={{ padding: "8px 10px" }}>
                  {e.resultado === "ok"
                    ? <Badge color={C.green}>OK</Badge>
                    : <Badge color={C.red}>Error</Badge>
                  }
                  {e.error && (
                    <div style={{ fontSize: 10, color: C.red, marginTop: 3, fontFamily: "monospace" }}>
                      {e.error.slice(0, 80)}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function UFWManager() {
  const [nodes,        setNodes]        = useState([]);
  const [selectedNode, setSelectedNode] = useState("");
  const [nodeData,     setNodeData]     = useState(null);  // { active, rules, ... }
  const [changelog,    setChangelog]    = useState([]);
  const [loadingNodes, setLoadingNodes] = useState(true);
  const [loadingRules, setLoadingRules] = useState(false);
  const [showAdd,      setShowAdd]      = useState(false);
  const [editRule,     setEditRule]     = useState(null);
  const [toggling,     setToggling]     = useState(false);
  const [errRules,     setErrRules]     = useState("");
  const [activeTab,    setActiveTab]    = useState("rules"); // "rules" | "log"

  // Cargar nodos
  const loadNodes = useCallback(async () => {
    setLoadingNodes(true);
    try {
      const d = await apiFetch("/nodes");
      const list = d.nodes || [];
      setNodes(list);
      // Auto-seleccionar el primer nodo si el actual no existe (evita pedir /rules/pbx → 404)
      setSelectedNode(prev => list.some(n => n.key === prev) ? prev : (list[0]?.key || ""));
    } catch(e) {
      console.error("Error cargando nodos:", e.message);
    } finally {
      setLoadingNodes(false);
    }
  }, []);

  // Cargar reglas del nodo seleccionado
  const loadRules = useCallback(async (nodeKey) => {
    setLoadingRules(true);
    setErrRules("");
    try {
      const d = await apiFetch(`/rules/${nodeKey}`);
      setNodeData(d);
    } catch(e) {
      setErrRules(e.message);
      setNodeData(null);
    } finally {
      setLoadingRules(false);
    }
  }, []);

  // Cargar changelog
  const loadChangelog = useCallback(async () => {
    try {
      const d = await apiFetch("/changelog?limit=100");
      setChangelog(d.data || []);
    } catch(e) {
      console.error("changelog error:", e.message);
    }
  }, []);

  useEffect(() => { loadNodes(); loadChangelog(); }, [loadNodes, loadChangelog]);
  useEffect(() => { if (selectedNode) loadRules(selectedNode); }, [selectedNode, loadRules]);

  async function handleDelete(num) {
    await apiFetch(`/rules/${selectedNode}/${num}`, { method: "DELETE" });
    await Promise.all([loadRules(selectedNode), loadNodes(), loadChangelog()]);
  }

  async function handleToggle() {
    setToggling(true);
    try {
      await apiFetch(`/toggle/${selectedNode}`, { method: "POST" });
      await Promise.all([loadRules(selectedNode), loadNodes(), loadChangelog()]);
    } catch(e) {
      alert(`Error: ${e.message}`);
    } finally {
      setToggling(false);
    }
  }

  const selectedNodeMeta = nodes.find(n => n.key === selectedNode) || {};

  return (
    <div style={{ padding: "28px 32px", minHeight: "100vh", background: C.bg, color: C.text }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <span style={{ fontSize: 22 }}>🔥</span>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>
            Firewall UFW
          </h1>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: C.muted }}>
          Gestión de reglas de firewall en todos los nodos de la infraestructura.
          Los cambios se aplican en tiempo real vía SSH.
        </p>
      </div>

      {/* Selector de nodo */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase",
          letterSpacing: ".5px", fontWeight: 600, marginBottom: 10 }}>
          Seleccionar nodo
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {loadingNodes ? (
            <span style={{ color: C.muted, fontSize: 13 }}>Cargando nodos…</span>
          ) : nodes.map(node => (
            <button
              key={node.key}
              onClick={() => setSelectedNode(node.key)}
              style={{
                padding: "9px 16px", borderRadius: 9, cursor: "pointer",
                border: `2px solid ${selectedNode === node.key ? C.accent : C.border}`,
                background: selectedNode === node.key ? `${C.accent}18` : C.surface,
                color: selectedNode === node.key ? C.accent : C.text,
                fontWeight: selectedNode === node.key ? 700 : 400,
                fontSize: 13, transition: "all .15s",
                display: "flex", alignItems: "center", gap: 8,
              }}>
              <span>{NODE_ICONS[node.key] || "🖥"}</span>
              <span>{node.name}</span>
              <span style={{
                width: 8, height: 8, borderRadius: "50%",
                background: node.active ? C.green
                           : node.status === "error" ? C.yellow : C.red,
                display: "inline-block",
              }}/>
              <span style={{ fontSize: 10, color: node.active ? C.green : C.red }}>
                {node.active ? "activo" : node.status === "error" ? "?" : "inactivo"}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Contenido principal */}
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 12, padding: 24, marginBottom: 24,
      }}>
        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20,
          borderBottom: `1px solid ${C.border}`, paddingBottom: 0 }}>
          {[
            { key: "rules",     label: "📋 Reglas activas" },
            { key: "whitelist", label: "✅ Whitelist" },
            { key: "blacklist", label: "⛔ Blacklist" },
            { key: "log",       label: "📜 Historial de cambios" },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              padding: "8px 16px", background: "none", cursor: "pointer",
              border: "none", fontSize: 13, fontWeight: 600,
              color: activeTab === tab.key ? C.accent : C.muted,
              borderBottom: `2px solid ${activeTab === tab.key ? C.accent : "transparent"}`,
              marginBottom: -1,
            }}>{tab.label}</button>
          ))}
        </div>

        {activeTab === "rules" && (
          <>
            {loadingRules ? (
              <div style={{ textAlign: "center", padding: "32px 0", color: C.muted }}>
                Cargando reglas de {selectedNodeMeta.name || selectedNode}…
              </div>
            ) : errRules ? (
              <div style={{
                padding: "14px 18px", borderRadius: 8,
                background: `${C.red}15`, border: `1px solid ${C.red}44`,
                color: C.red, fontSize: 13,
              }}>
                Error: {errRules}
              </div>
            ) : nodeData && (
              <RulesTable
                node={{ ...selectedNodeMeta, key: selectedNode }}
                rules={nodeData.rules || []}
                ufwActive={nodeData.active}
                onDelete={handleDelete}
                onEditClick={(rule) => { setEditRule(rule); setShowAdd(true); }}
                onAddClick={() => { setEditRule(null); setShowAdd(true); }}
                toggling={toggling}
                onToggle={handleToggle}
              />
            )}
          </>
        )}

        {activeTab === "log" && (
          <ChangelogTable entries={changelog} />
        )}

        {activeTab === "whitelist" && (
          <FirewallLists list="whitelist" onChanged={loadChangelog} />
        )}
        {activeTab === "blacklist" && (
          <FirewallLists list="blacklist" onChanged={loadChangelog} />
        )}
      </div>

      {/* Modal agregar regla */}
      {showAdd && nodeData && (
        <RuleModal
          nodeKey={selectedNode}
          nodeName={selectedNodeMeta.name || selectedNode}
          editRule={editRule}
          onSave={async () => {
            setShowAdd(false); setEditRule(null);
            await Promise.all([loadRules(selectedNode), loadNodes(), loadChangelog()]);
          }}
          onClose={() => { setShowAdd(false); setEditRule(null); }}
        />
      )}
    </div>
  );
}
